import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRevalidator } from 'react-router-dom';
import debug from 'debug';
import { useLanguage } from '../../../hooks/useLanguage';
import DropDownInput from '../DropDown/DropDownInput';
import { OptionSelected } from '../DropDown/DropDownList/types';

const log = debug('rois:components:language-selector');

if (import.meta.env.DEV) {
  debug.enable('rois:components:language-selector*');
}

interface LanguageSelectorProps {
  className?: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ className }) => {
  const { t } = useTranslation();
  const {
    currentLanguage,
    availableLanguages,
    changeLanguage,
    getLanguageName,
    isLoading,
  } = useLanguage();

  // Convertir idiomas disponibles a opciones para DropDownInput
  const languageOptions: OptionSelected[] = availableLanguages.map(
    (lang: string) => ({
      label: getLanguageName(lang),
      value: lang,
    })
  );

  // Encontrar la opción seleccionada actual
  const selectedOption = languageOptions.find(
    (option: OptionSelected) => option.value === currentLanguage
  );

  // 🔍 LOGS PARA DEBUG
  log('🌐 LanguageSelector Debug:', {
    currentLanguage,
    availableLanguages,
    languageOptions,
    selectedOption,
    selectedOptionLabel: selectedOption?.label,
    selectedOptionValue: selectedOption?.value,
    'defaultValue pasado al DropDown': selectedOption?.value,
  });

  const revalidator = useRevalidator();

  const handleLanguageChange = async (option: OptionSelected | undefined) => {
    if (!option) return;
    await changeLanguage(option.value);

    // Volver a ejecutar los loaders de la ruta activa.
    //
    // Buena parte de los textos que dependen del idioma NO se resuelven al
    // renderizar, sino dentro de los *selectors*, que corren en el loader de
    // React Router: el resultado queda cocido en los datos de la ruta. Cambiar
    // de idioma re-renderiza los componentes, pero NO vuelve a ejecutar los
    // loaders, así que esos textos se quedaban en el idioma anterior hasta que
    // navegabas a otra pantalla. Se veía crudo en el resumen de la actuación:
    // interfaz en catalán («Tipus vial», «Carril») con los valores en inglés
    // («Roadway», «Unspecified»). Reportado por el usuario 2026-08-04.
    //
    // `revalidate()` reejecuta los loaders de la ruta actual; Apollo sirve de
    // caché, así que no son viajes nuevos a la red — solo se rehacen las
    // traducciones. Se prefiere a recargar la página entera, que costaría el
    // arranque en frío completo.
    revalidator.revalidate();
  };

  return (
    <div className={className}>
      <DropDownInput
        key={currentLanguage} // Forzar re-mount cuando cambie el idioma
        inputSize="S"
        onChangeSelected={handleLanguageChange}
        typeDropDown="Default"
        placeholder={t('common:labels.select')}
        defaultValue={selectedOption?.value}
        options={languageOptions}
        isDisabled={isLoading}
      />
    </div>
  );
};

export default LanguageSelector;
